import logoAmazonMusic from "@/assets/images/logo-amazon-music.png";
import logoApplePodcast from "@/assets/images/logo-apple-podcast.svg";
import logoSpotify from "@/assets/images/logo-spotify.svg";
import logoYoutube from "@/assets/images/logo-youtube.svg";
import DummyLogo from "@/assets/images/lucidity-placeholder.png";
import Subscribe from "@/components/Subscribe";
import Layout from "@/components/elements/Layout";
import CarouselGuest from "@/components/parts/CarouselGuest";
import ExpertEpisodesModal from "@/components/modal/ExpertEpisodesModal";
import RelatedPodcast from "@/components/podcast/RelatedPodcast";
import { Button } from "@/components/ui/button";
import { env } from "@/next.config";
import { getDetailSeriesBySlug } from "@/services/api/Series";
import { cleanHTML } from "@/utils/content";
import httpUtils from "@/utils/httpUtils";
import { ArrowRight } from "@phosphor-icons/react/dist/ssr";
import dayjs from "dayjs";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useRef, useState } from "react";
import ReactPlayer from "react-player";
import TopStyle from "../top-style";

export default function SeriesDetail({ series, companies }) {
  const router = useRouter();
  const playerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);

  const handlePlayVideo = () => {
    setIsPlaying(true);
  };
  return (
    <Layout
      title={series.title}
      meta_title={series.meta_title}
      description={series.meta_description}
      meta_image={series.thumbnail_url}
    >
      <div className='relative lg:pt-24 lg:pb-24'>
        <TopStyle />
        <div className='container relative z-10'>
          <div className='items-start lg:flex lg:space-x-8'>
            <div className='relative lg:w-[70%]'>
              <div className='sticky z-10 -mx-4 overflow-hidden lg:mx-0 top-16 lg:top-0 lg:relative aspect-video lg:rounded-md'>
                {!isPlaying ? (
                  <>
                    <Button
                      className='absolute z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer top-1/2 left-1/2'
                      onClick={handlePlayVideo}
                    >
                      Watch Trailer
                    </Button>
                    <div className='w-full h-full bg-black'>
                      <Image
                        src={series.thumbnail_url || DummyLogo}
                        layout='fill'
                        alt='Video Poster'
                        objectFit='cover'
                        className='w-full h-full opacity-50'
                      />
                    </div>
                  </>
                ) : (
                  <ReactPlayer
                    ref={playerRef}
                    className='absolute top-0 left-0 react-player'
                    width='100%'
                    height='100%'
                    url={series.video_url}
                    controls={true}
                    playing={true}
                    config={{
                      file: { attributes: { controlsList: "nodownload" } },
                    }}
                    stopOnUnmount={false}
                    pip
                  />
                )}
              </div>
              <div className='mt-4 lg:mt-6'>
                <h1 className='text-xl font-bold leading-tight tracking-tight lg:leading-none lg:text-4xl'>
                  {series.title}
                </h1>
                <div className='inline-flex items-center mt-2 space-x-1 text-xs lg:text-sm lg:mt-3'>
                  <span className='inline-block font-medium text-transparent bg-clip-text bg-gradient-to-r from-yellow-1 to-red-1'>
                    {series.episodes.length}{" "}
                    {series.episodes.length === 1 ? "Episode" : "Episodes"}
                  </span>
                  <span className='opacity-30'>&bull;</span>
                  <span className='font-medium'>
                    {dayjs(series.publish_date).format("D MMMM YYYY")}
                  </span>
                </div>
              </div>
              {series.links.length > 0 && (
                <div className='flex items-center py-5 lg:py-6 space-x-3.5'>
                  <p className='text-xs text-white/50'>Also available on</p>

                  <div className='inline-flex items-center space-x-2'>
                    {/* Spotify Link */}
                    {series.links.map((link) => (
                      <>
                        {link.platform_name === "spotify" && (
                          <Link legacyBehavior key={link.id} href={link.url}>
                            <a
                              className='relative flex items-center overflow-hidden rounded-full size-7 lg:size-9'
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              <Image
                                src={logoSpotify}
                                alt='Spotify'
                                layout='fill'
                                className='object-cover'
                              />
                            </a>
                          </Link>
                        )}
                        {/* Apple Podcast Link */}
                        {link.platform_name === "apple_podcast" && (
                          <Link legacyBehavior key={link.id} href={link.url}>
                            <a
                              className='relative flex items-center overflow-hidden rounded-full size-7 lg:size-9'
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              <Image
                                src={logoApplePodcast}
                                alt='Apple Podcast'
                                layout='fill'
                                className='object-cover'
                              />
                            </a>
                          </Link>
                        )}
                        {/* YouTube Link */}
                        {link.platform_name === "youtube" && (
                          <Link legacyBehavior key={link.id} href={link.url}>
                            <a
                              className='relative flex items-center overflow-hidden rounded-full size-7 lg:size-9'
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              <Image
                                src={logoYoutube}
                                alt='YouTube'
                                layout='fill'
                                className='object-cover'
                              />
                            </a>
                          </Link>
                        )}
                        {/* Amazon Music Link */}
                        {link.platform_name === "amazon_music" && (
                          <Link legacyBehavior key={link.id} href={link.url}>
                            <a
                              className='relative flex items-center overflow-hidden rounded-full size-7 lg:size-9'
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              <Image
                                src={logoAmazonMusic}
                                alt='Amazon Music'
                                layout='fill'
                                className='object-cover'
                              />
                            </a>
                          </Link>
                        )}
                      </>
                    ))}
                  </div>
                </div>
              )}
              <div className='space-y-8 lg:space-y-12'>
                <div className='text-sm prose text-white lg:text-base prose-blue lg:leading-relaxed max-w-none'>
                  {cleanHTML(series.full_description)}
                </div>

                <div className='lg:hidden'>
                  <div className='flex items-center mb-3 space-x-2.5'>
                    <p className='font-semibold'>In this Series</p>
                    <div className='inline-flex items-center px-2.5 lg:px-3 py-0.5 lg:py-1.5 rounded-full bg-white/10'>
                      <span className='text-[10px] lg:text-xs font-semibold text-transparent bg-clip-text bg-gradient-to-r from-yellow-1 to-red-1'>
                        {series.episodes.length}{" "}
                        {series.episodes.length === 1 ? "Episode" : "Episodes"}
                      </span>
                    </div>
                  </div>
                  <div className='px-3 border divide-y rounded-lg divide-dashed divide-white/20'>
                    {series?.episodes.map((episode, index) => (
                      <RelatedPodcast
                        key={index}
                        episode={episode}
                        index={index}
                      />
                    ))}
                  </div>
                </div>

                {series.covered_topics.length > 0 && (
                  <div>
                    <div className='flex items-center space-x-3'>
                      <span className='text-xl font-medium lg:text-3xl font-display'>
                        Covered Topics
                      </span>
                      <span className='flex-1 hidden h-px lg:block bg-gradient-to-tr from-yellow-1 to-red-1'></span>
                    </div>
                    <div className='mt-5 text-sm prose text-white lg:text-base max-w-none'>
                      <ul>
                        {series.covered_topics.map((topic, index) => (
                          <li key={`topic-${topic.id}`}>{topic.title}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {series.special_guests.length > 0 && (
                  <div>
                    <CarouselGuest
                      title={"Special Guests"}
                      data={series.special_guests}
                      onSelect={setSelectedExpert}
                    />
                  </div>
                )}

                {companies.ventureCapitals.length > 0 && (
                  <div className='text-white'>
                    <div className='flex items-center space-x-3'>
                      <span className='text-xl font-medium lg:text-3xl font-display'>
                        Mentioned Investors
                      </span>
                      <span className='flex-1 h-px bg-gradient-to-tr from-yellow-1 to-red-1'></span>
                    </div>
                    <div className='max-w-3xl mt-3 space-y-1 lg:mt-5'>
                      {companies.ventureCapitals.map((investor, index) => (
                        <Link
                          href={`/investors/${investor.slug}-${investor.id}`}
                          key={investor.id}
                          className='flex flex-wrap items-center gap-3 p-3 rounded-lg lg:p-5 lg:flex-nowrap bg-white/10 hover:bg-white/15'
                        >
                          <div className='flex-1 space-y-1 leading-none'>
                            <p className='text-base font-semibold tracking-tight lg:text-lg'>
                              {investor.name}
                            </p>
                            <p className='mt-1.5 text-xs lg:text-sm leading-normal text-white/50 line-clamp-2'>
                              {investor.summary}
                            </p>
                          </div>

                          <div className='relative flex-shrink-0 h-12 overflow-hidden rounded-full lg:size-16 aspect-square'>
                            <Image
                              src={investor.logo_url || DummyLogo}
                              layout='fill'
                              alt={investor.name}
                              className='object-cover'
                            />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {companies.startups.length > 0 && (
                  <div className='text-white'>
                    <div className='flex items-center space-x-3'>
                      <span className='text-xl font-medium lg:text-3xl font-display'>
                        Mentioned Startups
                      </span>
                      <span className='flex-1 h-px bg-gradient-to-tr from-yellow-1 to-red-1'></span>
                    </div>
                    <div className='max-w-3xl mt-3 space-y-1 lg:mt-5'>
                      {companies.startups.map((startup, index) => (
                        <Link
                          href={`/startups/${startup.slug}-${startup.id}`}
                          key={startup.id}
                          className='flex flex-wrap items-center gap-3 p-3 rounded-lg lg:p-5 lg:flex-nowrap bg-white/10 hover:bg-white/15'
                        >
                          <div className='flex-1 space-y-1 leading-none'>
                            <p className='text-lg font-semibold tracking-tight lg:text-xl'>
                              {startup.name}
                            </p>
                            <p className='text-xs text-white/40 line-clamp-2'>
                              {startup.summary}
                            </p>
                          </div>

                          <div className='relative flex-shrink-0 h-12 overflow-hidden rounded-full lg:size-16 aspect-square'>
                            <Image
                              src={startup.logo_url || DummyLogo}
                              layout='fill'
                              alt={startup.name}
                              className='object-cover'
                            />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='lg:w-[30%] mt-8 lg:mt-0 space-y-8 lg:block'>
              <div className='hidden lg:block'>
                <div className='flex items-center mb-3 space-x-2.5'>
                  <p className='font-semibold'>In this Series</p>
                  <div className='inline-flex items-center px-3 py-1.5 rounded-full bg-white/10'>
                    <span className='text-xs font-medium text-transparent bg-clip-text bg-gradient-to-r from-yellow-1 to-red-1'>
                      {series.episodes.length}{" "}
                      {series.episodes.length === 1 ? "Episode" : "Episodes"}
                    </span>
                  </div>
                </div>
                <div className='px-3 max-h-[580px] overflow-y-auto border divide-y rounded-lg divide-dashed divide-white/20'>
                  {series?.episodes.map((episode, index) => (
                    <RelatedPodcast
                      key={index}
                      episode={episode}
                      index={index}
                    />
                  ))}
                </div>
              </div>
              <div>
                {/* <RelatedContentSidebar
                    title='Related Articles'
                    currentContentId={article.uuid}
                    contentType={CONTENT_TYPES.ARTICLES}
                    detailPrefix='articles'
                  /> */}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Subscribe />
      <ExpertEpisodesModal
        expert={selectedExpert}
        isOpen={!!selectedExpert}
        onOpenChange={(open) => {
          if (!open) setSelectedExpert(null);
        }}
      />
    </Layout>
  );
}

export async function getServerSideProps({ params }) {
  const slug = params.slug;
  const resSeries = await getDetailSeriesBySlug(slug);
  const series = resSeries?.data || [];

  async function getMentionedCompanies() {
    return httpUtils.get(
      `${env.API_VERSION}/podcast-series/${series.id}/pinned`
    );
  }
  const resCompanies = await getMentionedCompanies();
  const companies = resCompanies.data.data || [];

  return {
    props: {
      series,
      companies,
    },
  };
}
